import { builtEnvironment, IntakeDetectionResponse, isRunningLocal } from '@shared';
import type {
  AdvisorUsageEventRequest,
  FreemiumTier,
  NylPilotMetricsExportResponse,
  NylPilotMetricsExportRequest,
  NylPilotMetricsExportStartResponse,
  NylPilotMetricsResponse,
} from '@shared';
import { ApiClient } from './apiClient';
import getBaseUrl from '@shared/baseUrl';
import { API } from '@/api/types';

const origin = isRunningLocal() ? window.location.origin : getBaseUrl();
const env = isRunningLocal() ? 'dev' : builtEnvironment;
const waterlilyBaseURL = `${origin}/${env}/api`;

export const WaterlilyClient = new ApiClient({
  baseURL: waterlilyBaseURL,
  onUnauthorized: () => {
    WaterlilyClient.clearToken();
    window.location.href = '/auth/signout';
  },
});

export function createQueryKeys<T extends string>(resource: T) {
  return {
    all: [resource] as const,
    lists: () => [resource, 'list'] as const,
    list: <P extends Record<string, unknown>>(params: P) => [resource, 'list', { params }] as const,
    details: () => [resource, 'detail'] as const,
    detail: <K extends string>(id: K) => [resource, 'detail', id] as const,
  };
}

export const quotesApi = {
  query: (payload: FetchQuotesPayload) =>
    WaterlilyClient.post<QuotesApiResponse>('/quotes-query', payload),
};

/**
 * Records that an advisor proved control of their mailbox, after `/auth/email-verify`
 * redeems the emailed sign-in ticket.
 *
 * Takes no advisor id and no email address on purpose — the server reads both from the
 * authenticated session, so a caller cannot mark somebody else's mailbox as proven. See
 * `services/postEmailVerificationForAdvisor.ts`.
 */
export const advisorEmailVerificationApi = {
  record: (payload: {
    clerkSessionId?: string | null;
    conversationId?: string | null;
    userAgent?: string | null;
  }) =>
    WaterlilyClient.post<{ verified: boolean; emailAddress: string; expiresAt: string }>(
      '/advisor/email-verification',
      payload,
    ),

  /**
   * Redeems the verification-only token from an emailed link. What `/auth/email-verify` uses.
   *
   * Unauthenticated by design: the token is the proof, and the server derives the advisor and the
   * address from the token's row, never from anything sent here. Signs no one in. See
   * `services/postEmailVerificationTokenForPublic.ts`.
   */
  redeem: (payload: { token: string; userAgent?: string | null }) =>
    WaterlilyClient.post<{ verified: boolean; emailAddress: string; expiresAt: string }>(
      '/public/email-verification-token',
      payload,
    ),
};

/**
 * The public consent page's two calls. Unauthenticated by design: the recipient is a customer
 * who may have no Waterlily account, and the token is what identifies the case. Neither call
 * signs anyone in. See `services/postAgentEnteredConsentForPublic.ts`.
 */
export const agentEnteredConsentApi = {
  /** Read-only. Must not consume the token — mail scanners pre-fetch links. */
  lookup: (payload: { token: string }) =>
    WaterlilyClient.post<API.AgentEnteredConsentLookup>(
      '/public/agent-entered-consent-lookup',
      payload,
    ),
  submit: (payload: API.AgentEnteredConsentSubmit) =>
    WaterlilyClient.post<API.AgentEnteredConsentResult>('/public/agent-entered-consent', payload),
};

export const advisorLogoApi = {
  createUpload: (contentType: string) =>
    WaterlilyClient.post<{ url: string; advisorLogoKey: string }>('/advisor-logo-upload', {
      contentType,
    }),
  remove: () => WaterlilyClient.delete<{ advisorLogoKey: null }>('/advisor-logo'),
};

export const ccrcTypeAKeys = createQueryKeys('ccrcTypeA');

export const ccrcTypeAApi = {
  getProducts: () => WaterlilyClient.get<FetchCcrcTypeAProductsResponse>('/ccrc-type-a-products'),
};

export const consentDisclosureKeys = {
  all: ['consentDisclosure'] as const,
  detail: (key: string) => [...consentDisclosureKeys.all, key] as const,
};

export const consentDisclosureApi = {
  get: (clientId?: string) =>
    WaterlilyClient.get<API.ConsentDisclosureData>(
      '/consent-disclosure',
      clientId ? { clientId } : undefined,
    ),
};

export const distributionPartnerKeys = {
  all: ['distributionPartners'] as const,
  partners: () => [...distributionPartnerKeys.all, 'partners'] as const,
  contacts: (partnerKey: string) =>
    [...distributionPartnerKeys.all, 'contacts', partnerKey] as const,
  sharingConsent: () => [...distributionPartnerKeys.all, 'sharing-consent'] as const,
  hasLegacyClients: () => [...distributionPartnerKeys.all, 'has-legacy-clients'] as const,
  contactStatus: () => [...distributionPartnerKeys.all, 'contact-status'] as const,
};

export const distributionPartnersApi = {
  listPartners: () =>
    WaterlilyClient.get<API.DistributionPartnerSummary[]>('/distribution-partners'),
  getContacts: (partnerKey: string) =>
    WaterlilyClient.get<API.DistributionPartnerContact[]>(
      `/distribution-partner-contacts/${partnerKey}`,
    ),
  connect: (
    partnerKey: string,
    contactAdvisorIds: string[],
    autoShare: boolean,
    // When auto-sharing, whether to also share the advisor's EXISTING clients now. Defaults to true
    // for the standard flow. EDJ pilot advisors always connect with auto-sharing off.
    shareExistingClients?: boolean,
  ) =>
    WaterlilyClient.post<{ connectedContactAdvisorIds: string[] }>(
      `/distribution-partner-connections/${partnerKey}`,
      { contactAdvisorIds, autoShare, shareExistingClients },
    ),
  // Turn per-contact sharing on/off for an already-connected contact.
  setContactSharing: (partnerKey: string, contactAdvisorId: string, autoShare: boolean) =>
    WaterlilyClient.post<{ contactAdvisorId: string; autoShare: boolean }>(
      `/distribution-partner-share/${partnerKey}`,
      { contactAdvisorId, autoShare },
    ),
  // Share the advisor's earlier (legacy) clients with one connected contact.
  shareContactLegacyClients: (partnerKey: string, contactAdvisorId: string, share: boolean) =>
    WaterlilyClient.post<{ contactAdvisorId: string; legacyShared: boolean }>(
      `/distribution-partner-share-legacy/${partnerKey}`,
      { contactAdvisorId, share },
    ),
  disconnect: (
    partnerKey: string,
    contactAdvisorId: string,
    scope: API.DistributionPartnerDisconnectScope,
  ) =>
    WaterlilyClient.post<{ disconnected: true; scope: API.DistributionPartnerDisconnectScope }>(
      `/distribution-partner-disconnect/${partnerKey}`,
      { contactAdvisorId, scope },
    ),
  requestOther: (partnerKey: string, contactName: string, contactEmail: string) =>
    WaterlilyClient.post<{ requested: true }>(
      `/distribution-partner-contact-request/${partnerKey}`,
      { contactName, contactEmail },
    ),
  // Off-list invite: no partner chosen up front — the server matches the email domain to a partner
  // or auto-creates a pending one from it.
  requestContact: (contactName: string, contactEmail: string, organizationName?: string) =>
    WaterlilyClient.post<{ requested: true }>('/distribution-partner-contact-request', {
      contactName,
      contactEmail,
      organizationName,
    }),
  getSharingConsent: () =>
    WaterlilyClient.get<API.DistributionPartnerSharingConsent>(
      '/distribution-partner-sharing-consent',
    ),
  setSharingConsent: (hasConsented: boolean) =>
    WaterlilyClient.post<{ hasConsented: boolean }>('/distribution-partner-sharing-consent', {
      hasConsented,
    }),
  getHasLegacyClients: () =>
    WaterlilyClient.get<API.DistributionPartnerHasLegacyClients>(
      '/distribution-partner-has-legacy-clients',
    ),
  getContactStatus: () =>
    WaterlilyClient.get<API.DistributionPartnerContactStatus>(
      '/distribution-partner-contact-status',
    ),
  shareLegacyClients: (hasConsented: boolean) =>
    WaterlilyClient.post<{ hasConsented: boolean }>('/distribution-partner-legacy-client-share', {
      hasConsented,
    }),
  recordLegacyShareAttestation: (clientId: string, hasConsented: boolean) =>
    WaterlilyClient.post<{ hasConsented: boolean }>(
      `/distribution-partner-legacy-client-share-attestation/${clientId}`,
      { hasConsented },
    ),
};

export const wizardDistributionPartnerKeys = {
  all: ['wizardDistributionPartners'] as const,
  list: () => [...wizardDistributionPartnerKeys.all, 'list'] as const,
};

export const wizardDistributionPartnersApi = {
  list: () => WaterlilyClient.get<API.WizardDistributionPartner[]>('/wizard-distribution-partners'),
  create: (input: API.WizardDistributionPartnerInput) =>
    WaterlilyClient.post<API.WizardDistributionPartner>('/wizard-distribution-partner', input),
  update: (id: string, patch: API.WizardDistributionPartnerPatch) =>
    WaterlilyClient.put<API.WizardDistributionPartner>(`/wizard-distribution-partner/${id}`, patch),
  delete: (id: string) =>
    WaterlilyClient.delete<{ deleted: true }>(`/wizard-distribution-partner/${id}`),
};

export const wizardDistributionPartnerBlacklistKeys = {
  all: ['wizardDistributionPartnerBlacklist'] as const,
  list: () => [...wizardDistributionPartnerBlacklistKeys.all, 'list'] as const,
};

export const wizardDistributionPartnerBlacklistApi = {
  list: () =>
    WaterlilyClient.get<API.DistributionPartnerBlacklistEntry[]>(
      '/wizard-distribution-partner-blacklist',
    ),
  add: (email: string, note: string | null) =>
    WaterlilyClient.post<API.DistributionPartnerBlacklistEntry>(
      '/wizard-distribution-partner-blacklist',
      { email, note },
    ),
  remove: (id: string) =>
    WaterlilyClient.delete<{ deleted: true }>(`/wizard-distribution-partner-blacklist/${id}`),
};

export const wizardDistributionPartnerContactExclusionKeys = {
  all: ['wizardDistributionPartnerContactExclusion'] as const,
  list: () => [...wizardDistributionPartnerContactExclusionKeys.all, 'list'] as const,
};

export const wizardDistributionPartnerContactExclusionApi = {
  list: () =>
    WaterlilyClient.get<API.DistributionPartnerContactExclusionEntry[]>(
      '/wizard-distribution-partner-contact-exclusions',
    ),
  add: (email: string, note: string | null) =>
    WaterlilyClient.post<API.AddDistributionPartnerContactExclusionResult>(
      '/wizard-distribution-partner-contact-exclusion',
      { email, note },
    ),
  remove: (id: string) =>
    WaterlilyClient.delete<{ deleted: true }>(
      `/wizard-distribution-partner-contact-exclusion/${id}`,
    ),
};

export const clientKeys = createQueryKeys('client');

export const clientsApi = {
  list: (params: { page: number }) => WaterlilyClient.get<API.ClientList>('/clients', params),
  getById: (id: string) => WaterlilyClient.get<API.Client>(`/client/${id}`),
  updateById: (id: string, data: Partial<API.Client>) =>
    WaterlilyClient.put<API.Client>(`/client/${id}`, data),
  deleteById: (id: string) => WaterlilyClient.delete<{ success: boolean }>(`/client/${id}`),
  getCalculations: (params: { page: number }) =>
    WaterlilyClient.get<API.Client>(`/client/calculations`),
  shareWithSupervisors: (clientId: string, data: API.ShareClientRequest) =>
    WaterlilyClient.post<API.ShareClientResponse>(`/client/${clientId}/shared-clients`, data),
  getSharedSupervisors: (clientId: string) =>
    WaterlilyClient.get<API.ClientSharedSupervisors>(`/client/${clientId}/shared-supervisors`),
  unlinkPartnerFundingSource: (body: PutUnlinkPartnerFundingSourceBodyProps) =>
    WaterlilyClient.put<ClientContainer>(
      `/client/${body.clientId}/unlink-partner-funding-source`,
      body,
    ),
};

export const advisorAccountActivationKeys = {
  all: ['advisorAccountActivation'] as const,
  preflight: (overrideConnection?: 'email') =>
    [...advisorAccountActivationKeys.all, 'preflight', overrideConnection ?? 'default'] as const,
};

type AdvisorAccountActivationOptions = {
  overrideConnection?: 'email';
};

export const advisorAccountActivationApi = {
  getPreflight: ({ overrideConnection }: AdvisorAccountActivationOptions = {}) =>
    WaterlilyClient.get<API.AdvisorAccountActivationPreflight>(
      '/advisor-account-activation',
      overrideConnection ? { override_connection: overrideConnection } : undefined,
    ),
  activate: ({ overrideConnection }: AdvisorAccountActivationOptions = {}) =>
    WaterlilyClient.post<API.AdvisorAccountActivationResult>(
      '/advisor-account-activation',
      undefined,
      overrideConnection ? { params: { override_connection: overrideConnection } } : undefined,
    ),
};

export const advisorKeys = createQueryKeys('advisor');

export const advisorsApi = {
  list: (params: { page: number }) => WaterlilyClient.get<API.AdvisorList>('/advisors', params),
  getById: (id: string) => WaterlilyClient.get<API.Advisor>(`/advisor/${id}`),
  updateById: (id: string, data: AdvisorProfile) =>
    WaterlilyClient.post<Advisor>(`/advisor-updates/${id}`, data),
  putById: (id: string, data: Advisor) => WaterlilyClient.put<Advisor>(`/advisor/${id}`, data),
  // Syncs our DB email from the (already reverified + Clerk-verified) session. The server reads
  // the authoritative email from the session claims and requires fresh reverification; the body
  // email is advisory only. Reverification must have already happened client-side (HIPAA).
  updateEmail: (id: string, email: string) =>
    WaterlilyClient.put<{ advisorId: string; advisorEmail: string }>(`/advisor/${id}/email`, {
      email,
    }),
  saveShopInsuranceFilterDefaults: (
    advisorId: string,
    defaults: ShopInsuranceFilterDefaults | null,
  ) =>
    WaterlilyClient.post<Advisor>(`/advisor/${advisorId}/shop-insurance-filter-defaults`, {
      shopInsuranceFilterDefaults: defaults,
    }),
};

export const authKeys = createQueryKeys('auth');

export const authApi = {
  postAdvisorExistsForPublic: (data: AdvisorExistsCheckRequest) =>
    WaterlilyClient.post<AdvisorExistsCheckResponse>('/public/advisor-exists', data),
  postAdvisorSignupEligibilityForPublic: (data: API.AdvisorSignupEligibilityRequest) =>
    WaterlilyClient.post<API.AdvisorSignupEligibilityResponse>(
      '/public/advisor-signup-eligibility',
      data,
    ),
};

export const stripeKeys = createQueryKeys('stripe');

export const stripeApi = {
  createCheckoutSession: (data: {
    advisorId: string;
    priceId?: string;
    planType?: 'trial' | 'monthly' | 'annual';
    returnPath?: string;
    uiMode?: 'embedded' | 'hosted';
  }) =>
    WaterlilyClient.post<{ clientSecret: string | null; url: string | null }>(
      '/stripe-subscription-checkout-session-url',
      data,
    ),
};

export const clientStripeApi = {
  createCheckoutSession: (data: {
    clientAuthUserId: string;
    returnPath?: string;
    uiMode?: 'embedded' | 'hosted';
  }) =>
    WaterlilyClient.post<{ clientSecret: string | null; url: string | null }>(
      '/public/client-auth-user-stripe-checkout-session',
      data,
    ),
};

export const freemiumKeys = {
  all: ['freemium'] as const,
  planningUnlock: (clientId: string) => [...freemiumKeys.all, 'planning-unlock', clientId] as const,
  advisorAccess: (advisorId: string) => [...freemiumKeys.all, 'advisor-access', advisorId] as const,
};

export const freemiumApi = {
  getPlanningUnlock: (clientId: string) =>
    WaterlilyClient.get<{ isUnlocked: boolean; handoffPending: boolean }>(
      `/client/${clientId}/planning-unlock`,
    ),
  postSelection: (
    clientId: string,
    data: {
      intent: string;
      tier?: FreemiumTier;
      reflexiveAnswer?: string;
      details?: string;
      returnPath?: string;
      uiMode?: 'embedded' | 'hosted';
    },
  ) =>
    WaterlilyClient.post<API.FreemiumSelectionResponse>(
      `/client/${clientId}/freemium-selection`,
      data,
    ),
  postDetails: (clientId: string, data: { details: string }) =>
    WaterlilyClient.post<{ ok: boolean }>(`/client/${clientId}/freemium-details`, data),
  postBgaInquiry: (advisorId: string, data: { bgaName: string }) =>
    WaterlilyClient.post<{ ok: boolean }>(`/advisor/${advisorId}/freemium-bga-inquiry`, data),
  getAdvisorPlanningAccess: (advisorId: string) =>
    WaterlilyClient.get<{ hasUnlimited: boolean }>(
      `/advisor/${advisorId}/freemium-planning-access`,
    ),
  openUnlimitedBillingPortal: (advisorId: string) =>
    WaterlilyClient.post<{ url: string | null }>(
      `/advisor/${advisorId}/freemium-planning-billing-portal`,
      {},
    ),
};

export const advisorOnboardingKeys = {
  all: ['advisorOnboarding'] as const,
  status: (advisorId: string) => [...advisorOnboardingKeys.all, 'status', advisorId] as const,
  questionnaire: (advisorId: string) =>
    [...advisorOnboardingKeys.all, 'questionnaire', advisorId] as const,
};

export const advisorOnboardingApi = {
  getStatus: (advisorId: string) =>
    WaterlilyClient.get<AdvisorOnboardingStatus>(`/advisor-onboarding-status/${advisorId}`),

  /**
   * Mark a client-triggered onboarding step as complete.
   * Only accepts steps that cannot be computed from data: 'welcome', 'review-results'
   */
  markStepComplete: (data: { advisorId: string; step: AdvisorOnboardingClientTriggeredStep }) =>
    WaterlilyClient.post<{ success: boolean; step: string; completedSteps: string[] }>(
      `/advisor-onboarding-complete-step/${data.advisorId}`,
      { step: data.step },
    ),

  getQuestionnaire: () =>
    WaterlilyClient.get<API.AdvisorOnboardingQuestionnairePayload>(
      '/advisor-onboarding-questionnaire',
    ),

  putQuestionnaire: (patch: Partial<import('@shared').QuestionnaireAnswers>) =>
    WaterlilyClient.put<API.AdvisorOnboardingQuestionnaireMutationResult>(
      '/advisor-onboarding-questionnaire',
      { patch },
    ),

  sendTwoPager: (advisorId: string) =>
    WaterlilyClient.post<void>(`/advisor/${advisorId}/two-pager-email`, {}),
};

export type SubscriptionSeatsResponse = {
  subscription: {
    stripeSubscriptionId: string;
    subscriptionIdRef: string;
    subscriptionStatus: string;
    totalSeats: number;
    ownerAdvisorId: string | null;
    unitPricePerSeat?: number | null;
    billingInterval?: string | null;
    trialEndDateTime?: Date | null;
    nextBillingDateTime?: Date | null;
    nextInvoiceAmount?: number | null;
    nextInvoiceDate?: Date | null;
  } | null;
  customer: {
    creditBalance?: number | null;
  } | null;
  seats: Array<
    SubscriptionSeat & {
      advisorEmail: string | null;
      advisorName: string | null;
    }
  >;
  isOwner: boolean;
  ownerInfo: {
    advisorName: string | null;
    advisorEmail: string | null;
    organization: string | null;
  } | null;
  currentAdvisorAssignedDateTime: Date | null;
  currentAdvisorExpiresDateTime: Date | null;
  pendingSeatInvitations: PendingSeatInvitation[] | null;
};

export const subscriptionSeatsKeys = {
  all: ['subscriptionSeats'] as const,
  byAdvisor: (advisorId: string) => [...subscriptionSeatsKeys.all, advisorId] as const,
};

export const subscriptionSeatsApi = {
  getByAdvisorId: (advisorId: string) =>
    WaterlilyClient.get<SubscriptionSeatsResponse>(`/subscription-seats/${advisorId}`),
};

export const advisorConsentsKeys = {
  all: ['advisorConsents'] as const,
  byAdvisor: (advisorId: string) => [...advisorConsentsKeys.all, advisorId] as const,
};

export type AdvisorConsent = {
  advisorConsentId: string;
  advisorId: string;
  consentText: string;
  consentVersionDescription: string;
  consentDateTime: string;
  ipAddress?: string | null;
  userAgent?: string | null;
};

export type PutAdvisorConsentsRequest = {
  advisorId: string;
  consents: Array<{
    consentText: string;
    consentVersionDescription: string;
    ipAddress?: string | null;
    userAgent?: string | null;
  }>;
};

export type PutAdvisorConsentsResponse = {
  success: boolean;
  message: string;
  results: AdvisorConsent[];
};

export const advisorConsentsApi = {
  getByAdvisorId: (advisorId: string) =>
    WaterlilyClient.get<AdvisorConsent[]>(`/advisor-consents/${advisorId}`),

  saveConsents: (data: PutAdvisorConsentsRequest) =>
    WaterlilyClient.put<PutAdvisorConsentsResponse>(`/advisor-consents/${data.advisorId}`, data),
};

export const pdfReportApi = {
  emailToAdvisor: (
    advisorId: string,
    data: {
      pdfBase64: string;
      clientEmail: string;
      clientName: string;
      filename: string;
      subject?: string;
    },
  ) => WaterlilyClient.post<void>(`/advisor/${advisorId}/pdf-report-email`, data),
};

export const pdfAuditApi = {
  log: (clientId: string, data: API.PdfAuditLogRequest) =>
    WaterlilyClient.post<API.PdfAuditLogResponse>(`/client/${clientId}/pdf-audit-log`, data),
};

export const shortFormIntakeKeys = createQueryKeys('shortFormIntake');
export const licenseKeys = createQueryKeys('licenses');

export const illustrationMonitorKeys = createQueryKeys('illustrationMonitor');

export const clientIllustrationKeys = createQueryKeys('clientIllustrations');

export const shortFormIntakeApi = {
  create: (data: ShortFormIntakeRequest) =>
    WaterlilyClient.post<ShortFormIntakeResponse>('/short-form-intake', data),
  createCouple: (data: ShortFormCoupleIntakeRequest) =>
    WaterlilyClient.post<ShortFormCoupleIntakeResponse>('/short-form-couple-intake', data),
};

export const partnerKeys = createQueryKeys('partner');

export const partnerApi = {
  addPartner: (clientId: string, data: PostAddPartnerRequest) =>
    WaterlilyClient.post<PostAddPartnerResponse>(`/client/${clientId}/add-partner`, data),
  invitePartner: (data: PutPartnerLinkRequestEmailProps) =>
    WaterlilyClient.put<void>('/partner-link-request-email', data),
};

export const quickEditApi = {
  save: (clientId: string, fields: Record<string, string | number | null>) =>
    WaterlilyClient.put<QuickEditIntakeResponse>(`/client/${clientId}/quick-edit-intake`, {
      clientId,
      fields,
    }),
};

export const quickEstimateApi = {
  /** Re-runs an Edward Jones quick estimate from its saved answers ("Retry Estimate"). */
  recalculate: (clientId: string) =>
    WaterlilyClient.post<ClientContainer>(`/client/${clientId}/recalculate-quick-estimate`, {}),
};

export const docusignInspectorKeys = createQueryKeys('docusignInspector');

export type DocusignTabsPreviewParams = {
  formId: string;
  clientPolicyApplicationId: string;
  forClientId: string;
  policyType?: string;
};

export type DocusignTabsPreviewResponse = {
  tabs: {
    textTabs: { tabLabel: string; value: string }[];
    checkboxTabs: { tabLabel: string; selected: 'true' | 'false' }[];
    radioGroupTabs: {
      groupName: string;
      radios: { value: string; selected: 'true' | 'false' }[];
    }[];
  };
  overflowFields: { questionRef: string; value: string }[];
  questionTitles: Record<string, string>;
};

export const docusignInspectorApi = {
  getTabsPreview: (clientId: string, params: DocusignTabsPreviewParams) =>
    WaterlilyClient.get<DocusignTabsPreviewResponse>(
      `/client/${clientId}/docusign-tabs-preview`,
      params,
    ),
  getTemplateTabs: (formId: string) =>
    WaterlilyClient.get<DocusignTemplateTabsResponse>(`/docusign-template-tabs/${formId}`),
};

export const quoteWaitlistApi = {
  signup: (data: API.QuoteWaitlistSignupRequest) =>
    WaterlilyClient.post<{ id: string }>('/quote-waitlist-signup', data),
};

export const anonymousPuiApi = {
  fetchAnonymizationRequestId(
    clientId: string,
    includeClientPartner: boolean,
  ): Promise<AnonymousPuiRequestIdResponse> {
    const params = includeClientPartner ? { includeClientPartner: 'true' } : {};
    return WaterlilyClient.get<AnonymousPuiRequestIdResponse>(
      `/client/${clientId}/anonymized-pui-request`,
      params,
    );
  },
  fetchHistory(clientId: string): Promise<PUIHistoryItem[]> {
    return WaterlilyClient.get<PUIHistoryItem[]>(`/client/${clientId}/anonymized-pui-history`);
  },
  postEmail(params: PostAnonymousPuiEmailRequestParams): Promise<PostAnonymousPuiEmailResponse> {
    return WaterlilyClient.post<PostAnonymousPuiEmailResponse>(`/anonymized-pui-email`, params);
  },
};

export const intakeDetectionKeys = createQueryKeys('intakeDetection');

export const intakeDetectionApi = {
  getIntakeDetection(clientId: string): Promise<IntakeDetectionResponse> {
    return WaterlilyClient.get<IntakeDetectionResponse>(`/client/${clientId}/intake-type`);
  },
};

export const policyDocumentsApi = {
  getDownloadUrl: (clientId: string, requestId: string) =>
    WaterlilyClient.post<{ url: string; fileName: string }>(
      `/client/${clientId}/policy-document-download-url`,
      { requestId },
    ),
};

export const openAiIntegrationKeys = createQueryKeys('openAiIntegration');

export const openAiIntegrationApi = {
  fetchCompletion: (requestId: string) =>
    WaterlilyClient.get<OpenAiCompletionResponseDbRecord>(`/open-ai-completion/${requestId}`),
  triggerUnderwriting: (requestBody: TriggerUnderwritingRequest) =>
    WaterlilyClient.post<string>(`/trigger-underwriting`, requestBody),
};

export const wizardAdvosorsMetricsKeys = createQueryKeys('wizardAdvisorsMetrics');

export const wizardAdvisorsMetricsApi = {
  getMetrics: () => WaterlilyClient.get<AdvisorMetricsDump>('/wizard-advisors-metrics'),
};

export const nylMetricsKeys = createQueryKeys('nylMetrics');

export const nylMetricsApi = {
  getMetrics: (params?: {
    from?: string;
    to?: string;
    advisorId?: string;
    scope?: 'nyl' | 'all';
  }) => WaterlilyClient.get<NylPilotMetricsResponse>('/nyl-pilot-metrics', params),
  recordActiveTime: (payload: AdvisorUsageEventRequest) =>
    WaterlilyClient.post<{ accepted: true; eventId: string }>('/advisor-usage-event', payload),
  startClientExport: (filters: NylPilotMetricsExportRequest) =>
    WaterlilyClient.post<NylPilotMetricsExportStartResponse>('/nyl-pilot-metrics-export', filters),
  getClientExport: (exportId: string) =>
    WaterlilyClient.get<NylPilotMetricsExportResponse>(`/nyl-pilot-metrics-export/${exportId}`),
};

export const underwritingResultsKeys = createQueryKeys('underwritingResults');

// =====================================================
// Underwriting Results API
// =====================================================

export const underwritingApi = {
  /**
   * Fetch cached underwriting eligibility results for all products.
   * Returns a map of productName to eligibility status.
   */
  getResults: (clientId: string) =>
    WaterlilyClient.get<UnderwritingResultsResponse>(`/client/${clientId}/underwriting-results`),
};

export const prefillReviewKeys = createQueryKeys('prefillReview');

// =====================================================
// Prefill Review API
// =====================================================

export interface PrefillReviewResponse {
  hasPrefill: boolean;
  hasCompletedPrefillReview: boolean;
  prefilledFields: Record<string, string | string[]>;
  advisorFirstName: string | null;
  advisorLastName: string | null;
  advisorFirmName: string | null;
}

export interface PrefillReviewCompletionResponse {
  success: boolean;
  prefillReviewCompletedAt: string;
}

export const prefillReviewApi = {
  get: (formReferenceId: string) =>
    WaterlilyClient.get<PrefillReviewResponse>(`/prefill-review/${formReferenceId}`),
  complete: (formReferenceId: string) =>
    WaterlilyClient.post<PrefillReviewCompletionResponse>(
      `/prefill-review-completion/${formReferenceId}`,
    ),
};

export type DocusignTemplateTabEntry = {
  tabLabel: string;
  required: string;
  locked: string;
};

export type DocusignTemplateRadioGroupEntry = {
  groupName: string;
  radios: { value: string }[];
};

export type DocusignTemplateSignerTabs = {
  textTabs: DocusignTemplateTabEntry[];
  checkboxTabs: DocusignTemplateTabEntry[];
  radioGroupTabs: DocusignTemplateRadioGroupEntry[];
  signHereTabs: { tabLabel: string }[];
  initialHereTabs: { tabLabel: string }[];
  dateTabs: { tabLabel: string; required: string }[];
  fullNameTabs: { tabLabel: string }[];
};

export type DocusignTemplateSignerResponse = {
  recipientId: string;
  roleName: string;
  tabs: DocusignTemplateSignerTabs;
};

export type DocusignTemplateTabsResponse = {
  templateId: string;
  templateName: string;
  signers: DocusignTemplateSignerResponse[];
  templates?: { templateId: string; templateName: string }[];
};

export type TriggerUnderwritingRequest = {
  clientId: string;
  fundingPolicyType: FundingPolicyType;
  formData?: PolicyFormSubmissionData;
};
